import * as React from "react";
import { toast } from "react-toastify";
import { CellInfo } from "react-table";

import { FilterParams } from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import { Modal } from "src/library/components/Modal";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PaginatedParams } from "src/core/shared/api";
import { PageProps } from "src/pages/Router/routes";
import {
  useOpenDaysApi,
  IOpenDay
} from "src/modules/shutdowns/api/open-day.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { openDaysTableColumns } from "src/core/shared/components/tables/openDaysTableColumns";

const defaultPagination: PaginatedParams<IOpenDay> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "startDate", "DESC" ] ]
};

/**
 * View OpenDays
 *
 */
const OpenDaysPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { history } = props;
  const TableRef = React.useRef<ITableInstance<IOpenDay>>(null);
  const [ openDaysResponse, getOpenDays ] = useOpenDaysApi("LIST_OPEN_DAYS");
  const openDaysDate = openDaysResponse.data;
  const [ deleteOpenDayResponse, deleteOpenDay ] = useOpenDaysApi("DELETE_OPEN_DAY");
  const [ modalData, setModalData ] = React.useState<number | null>(null);
  const checkPermission = useCheckUserPermission();
  const canCreate = checkPermission("create-global-settings");
  const canAmend = checkPermission("amend-global-settings");
  const canDelete = checkPermission("delete-global-settings");

  const onFetchData = React.useCallback((filters: FilterParams<IOpenDay>, pagination: PaginatedParams<IOpenDay>) => {
    getOpenDays({
      pagination,
      filters
    });
  }, [ getOpenDays ]);

  const editOpenDayAction = React.useCallback((id: number) => {
    history.push(`/platform/open-days/edit/${id}`);
  }, [ history ]);

  const deleteOpenDayAction = (id: number) => {
    setModalData(id);
  };

  const deleteOpenDayActionConfirm = () => {
    if (modalData) {
      deleteOpenDay(modalData);
      setModalData(null);
    }
  };

  // On open day deletion
  React.useEffect(() => {
    if (deleteOpenDayResponse.data) {
      toast.success("The open day has been successfully deleted.");

      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }

    if (deleteOpenDayResponse.error) {
      toast.error(`Could not delete open day - ${deleteOpenDayResponse.error.message}`);
    }
  }, [ deleteOpenDayResponse ]);

  /**
   * Table actions
   */
  const tableActions = React.useCallback((row: CellInfo) => {
    const actions = [];

    if (canAmend) {
      actions.push({
        title: "Edit",
        action: () => editOpenDayAction(row.original.id)
      });
    }

    if (canDelete) {
      actions.push({
        title: "Delete",
        action: () => deleteOpenDayAction(row.original.id)
      });
    }

    return actions;
  }, [
    canAmend,
    canDelete,
    editOpenDayAction
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper
        fill
        Header={canCreate && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={[
              <Button
                key="new-open-day"
                onClick={() => history.push("/platform/open-days/new")}
              >
                New open day
              </Button>
            ]}
          />
        )}
      >
        <Table
          tableId="global"
          ref={TableRef}
          error={openDaysResponse.error}
          loading={openDaysResponse.pending}
          tableOf="OpenDays"
          data={openDaysDate ? openDaysDate.result : []}
          onFetchData={onFetchData}
          totalRows={openDaysDate ? openDaysDate.count : 0}
          pages={openDaysDate ? openDaysDate.pages : 0}
          defaultPagination={defaultPagination}
          mainAction={row => editOpenDayAction(row.original.id)}
          columns={openDaysTableColumns(row => {
            const actions = tableActions(row);

            return actions.length ? (
              <ActionButton
                title="Actions"
                items={tableActions(row)}
              />
            ) : null;
          })}
        />
      </MainContentWrapper>

      <Modal
        small
        show={modalData !== null}
        type="confirm"
        title="Are you sure?"
        body="Deleting this open day cannot be undone."
        backgroundAction={() => setModalData(null)}
        actions={[
          {
            name: "No, cancel",
            action: () => setModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "I confirm",
            action: deleteOpenDayActionConfirm,
            $buttonType: "primary"
          }
        ]}
      />
    </Layout>
  );
};

export default OpenDaysPage;
